import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { EpaChemicalCustomName } from '../data-models/epa-chemical-custom-name.model';
import { EpaChemicalLabel } from '../data-models/epa-chemical-label.model';
import { EpaChemical } from '../data-models/epa-chemical.model';
import { AddCustomNameRequest } from '../request-models/add-custom-name-request.model';
import { ChemicalLabelRequest } from '../request-models/chemical-label-request.model';
import { EpaChemicalFilters } from '../request-models/epa-chemical-filters.model';
import { EpaChemicalUpdateRequest } from '../request-models/epa-chemical-update-request.model';
import { EpaPplsSyncResult } from '../response-models/epa-ppls-sync-result.model';
import { EpaSyncCancelResult } from '../response-models/epa-sync-cancel-result.model';
import { EpaSyncStatus } from '../response-models/epa-sync-status.model';
import { Page } from '../response-models/page.model';
import { TriggerSyncResponse } from '../response-models/trigger-sync-response.model';
import { toEpaChemicalRequest } from '../utils/epa-chemical-request-utils';
import { isRecord } from '../utils/is-record';
import { idString, nullableBoolean, nullableNumber, nullableString } from '../utils/wire-value-utils';

/**
 * EPA-synced chemicals: `/admin/epa-chemicals` (hub `admin-epa-chemicals-controller.ts`), paged with `limit`.
 * EPA fields are read-only; only `sdsUrl`, `adminNotes` and `isHidden` can be changed. Also custom names,
 * labels, the EPA sync job and the PPLS backfill.
 */
@Injectable({ providedIn: 'root' })
export class EpaChemicalsService extends BaseService<EpaChemical> {
  constructor() {
    super('admin/epa-chemicals', EpaChemical);
  }

  /** GET /admin/epa-chemicals: one page of EPA chemicals. Blank filters are not sent. */
  search(filters: EpaChemicalFilters = {}): Observable<Page<EpaChemical>> {
    return this.getPage({ query: toSearchQuery(filters) });
  }

  /** GET /admin/epa-chemicals/:id with custom names and labels. Emits `null` when the hub returns no data. */
  getById(id: string): Observable<EpaChemical | null> {
    return this.get(id);
  }

  /** PUT /admin/epa-chemicals/:id with only `sdsUrl`, `adminNotes` and `isHidden` (those that are set). */
  updateCustomFields(id: string, fields: EpaChemicalUpdateRequest): Observable<EpaChemical | null> {
    return this.update(id, toEpaChemicalRequest(fields));
  }

  /** POST /admin/epa-chemicals/:id/custom-names. A duplicate name fails with status 409. */
  addCustomName(chemicalId: string, name: AddCustomNameRequest): Observable<EpaChemicalCustomName | null> {
    const body = { name: name.name, nameType: name.nameType, isPrimary: name.isPrimary };
    return this.request('POST', { id: chemicalId, path: 'custom-names' }, toCustomNameOrNull, body);
  }

  /** DELETE /admin/epa-chemicals/:id/custom-names/:nameId */
  removeCustomName(chemicalId: string, nameId: string): Observable<void> {
    return this.request('DELETE', { id: chemicalId, path: ['custom-names', nameId] }, () => undefined);
  }

  /** POST /admin/epa-chemicals/sync: queues a full EPA sync. */
  triggerSync(): Observable<TriggerSyncResponse> {
    return this.request('POST', { path: 'sync' }, toTriggerSync, {});
  }

  /** POST /admin/epa-chemicals/ppls-backfill: re-enriches chemicals from PPLS without a full sync. */
  triggerPplsBackfill(): Observable<TriggerSyncResponse> {
    return this.request('POST', { path: 'ppls-backfill' }, toTriggerSync, {});
  }

  /** DELETE /admin/epa-chemicals/sync-job: cancels (or removes a stale) sync job; the current one without `jobId`. */
  cancelSyncJob(jobId?: string): Observable<EpaSyncCancelResult> {
    return this.request('DELETE', { path: 'sync-job', query: { jobId: jobId || undefined } }, toCancelResult);
  }

  /** GET /admin/epa-chemicals/sync-status: the current or last sync job. */
  getSyncStatus(): Observable<EpaSyncStatus> {
    return this.request('GET', { path: 'sync-status' }, toSyncStatus);
  }

  /**
   * POST /admin/epa-chemicals/:id/sync-ppls: re-enriches one chemical from PPLS. When PPLS had nothing new the
   * hub nests the record under `chemical`; either way this emits the chemical plus the hub's `message`.
   */
  syncFromPpls(chemicalId: string): Observable<EpaPplsSyncResult> {
    return this.request('POST', { id: chemicalId, path: 'sync-ppls' }, toPplsSyncResult, {});
  }

  /** POST /admin/epa-chemicals/:id/labels. Emits `null` when the hub returns no label. */
  addLabel(chemicalId: string, label: ChemicalLabelRequest): Observable<EpaChemicalLabel | null> {
    const body = { labelUrl: label.labelUrl, labelName: label.labelName, acceptedDate: label.acceptedDate, setAsCurrent: label.setAsCurrent };
    return this.request('POST', { id: chemicalId, path: 'labels' }, toLabelOrNull, body);
  }

  /** DELETE /admin/epa-chemicals/:id/labels/:labelId */
  deleteLabel(chemicalId: string, labelId: string): Observable<void> {
    return this.request('DELETE', { id: chemicalId, path: ['labels', labelId] }, () => undefined);
  }

  /** PUT /admin/epa-chemicals/:id/labels/:labelId/set-current: toggles the label; returns it and all labels. */
  setCurrentLabel(chemicalId: string, labelId: string): Observable<{ label: EpaChemicalLabel | null; labels: EpaChemicalLabel[] }> {
    return this.request('PUT', { id: chemicalId, path: ['labels', labelId, 'set-current'] }, toLabelToggle, {});
  }
}

function toSearchQuery(filters: EpaChemicalFilters): Record<string, string | number | boolean | undefined> {
  return {
    page: filters.page,
    limit: filters.limit,
    query: filters.query?.trim() || undefined,
    category: filters.category || undefined,
    signalWord: filters.signalWord || undefined,
    status: filters.status || undefined,
    rupFlag: filters.rupFlag ?? undefined,
    isHidden: filters.isHidden ?? undefined,
  };
}

function toLabelOrNull(data: unknown): EpaChemicalLabel | null {
  return isRecord(data) ? new EpaChemicalLabel(data) : null;
}

function toLabelToggle(data: unknown): { label: EpaChemicalLabel | null; labels: EpaChemicalLabel[] } {
  const source = isRecord(data) ? data : {};
  const labels = source['labels'];
  return {
    label: toLabelOrNull(source['label']),
    labels: Array.isArray(labels) ? labels.map(item => new EpaChemicalLabel(item)) : [],
  };
}

function toCustomNameOrNull(data: unknown): EpaChemicalCustomName | null {
  return isRecord(data) ? new EpaChemicalCustomName(data) : null;
}

function toTriggerSync(data: unknown): TriggerSyncResponse {
  const source = isRecord(data) ? data : {};
  return { jobId: idString(source['jobId']), message: nullableString(source['message']) ?? '' };
}

function toCancelResult(data: unknown): EpaSyncCancelResult {
  const source = isRecord(data) ? data : {};
  return {
    message: nullableString(source['message']) ?? '',
    jobId: idString(source['jobId']),
    removed: nullableBoolean(source['removed']) ?? false,
    cancelRequested: nullableBoolean(source['cancelRequested']) ?? false,
  };
}

function toSyncStatus(data: unknown): EpaSyncStatus {
  const source = isRecord(data) ? data : {};
  return {
    isRunning: nullableBoolean(source['isRunning']) ?? false,
    progress: nullableNumber(source['progress']) ?? null,
    currentJobId: nullableString(source['currentJobId']) ?? null,
    lastSyncAt: nullableString(source['lastSyncAt']) ?? null,
    lastSyncStatus: (nullableString(source['lastSyncStatus']) ?? null) as EpaSyncStatus['lastSyncStatus'],
    lastSyncStats: toSyncStats(source['lastSyncStats']),
    nextScheduledSync: nullableString(source['nextScheduledSync']) ?? null,
  };
}

function toSyncStats(value: unknown): EpaSyncStatus['lastSyncStats'] {
  if (!isRecord(value)) {
    return null;
  }
  return {
    added: nullableNumber(value['added']) ?? 0,
    updated: nullableNumber(value['updated']) ?? 0,
    unchanged: nullableNumber(value['unchanged']) ?? 0,
    removed: nullableNumber(value['removed']) ?? 0,
    errors: nullableNumber(value['errors']) ?? 0,
  };
}

function toPplsSyncResult(data: unknown): EpaPplsSyncResult {
  if (!isRecord(data)) {
    return { chemical: null, message: null };
  }
  const record = isRecord(data['chemical']) ? data['chemical'] : data;
  return { chemical: new EpaChemical(record), message: nullableString(data['message']) ?? null };
}
